import requiresAgreement from 'core/controllerPlans/delayed-effective-date/requires-agreement'
import { numberToCurrency } from '@hc-frontend/utils'
import tooltipMessage from 'core/controllerPlans/tooltipMessage'
import textContent from 'utils/DOM/textContent'
import toggleClass from 'utils/DOM/toggleClass'
import { CORE, SURE_CARE_NAME, VISION, VISION_ID } from 'core/constants/products'
import moneyFormat from 'utils/format/money'
import isNumber from 'utils/number/isNumber'
import forEach from 'utils/DOM/forEach'
import tooltip from 'core/DOM/tooltip'
import getById from 'utils/DOM/getById'
import getExtendedMessage from 'utils/pivot/plan/getExtendedMessage'
import innerHTML from 'utils/DOM/innerHTML'
import getExtendDuration from 'utils/pivot/plan/getExtendDuration'
import getExtendedTagMessage from 'utils/pivot/plan/getExtendedTagMessage'
import {
  updateDentalControl,
  updateLatitudeControl,
  updateVisionControl
} from './prepareDataToTrack'
import { getLatitudeFees } from 'core/controllerPlans/getLatitudeFees'
import detailsDentalFees from 'core/controllerPlans/detailsDentalFees'
import getCoverageLabel from 'utils/plans/getCoverageLabel'
import getEndDateLabel from 'utils/plans/getEndDateLabel'

const asMoneyWithoutDecimal = numberToCurrency
const asMoney = moneyFormat

function updateDOM(state: Pivot.State) {
  const hcStorage = state.hcStorage
  const plan = state.plans[state.planId]
  const coreBenefits = plan.coreBenefits
  const { period, policies } = state.filters
  const totalAddOns = state.addOns.length
  const prices = state.prices
  const durationDetails = plan.durations[period][policies].monthly

  const enrollmentFee = durationDetails.fees
    .filter(({ name }) => name === 'Application Fee')
    .reduce((acc, fee) => acc + fee.value, 0)

  textContent('.js-prices-plan', asMoney(durationDetails.premium))
  textContent('.js-prices-fee', asMoney(enrollmentFee))
  textContent('.js-plan-deductible', asMoneyWithoutDecimal(+coreBenefits.deductible))

  const moop = +coreBenefits.outOfPocketMax
  textContent(
    '.js-plan-moop',
    isNumber(moop) ? asMoneyWithoutDecimal(+coreBenefits.outOfPocketMax) : 'N/A'
  )

  textContent('.js-plan-coinsurance', coreBenefits.userCoinsurance + '%')
  textContent('.js-plan-coverage', asMoneyWithoutDecimal(+coreBenefits.lifetimeMax))

  textContent('.js-plan-core', plan.productId === CORE ? 'outpatient' : '')
  textContent('.js-total-products', totalAddOns + 1)
  textContent(
    '.js-plan-provider-network',
    `${plan.coreBenefits.networkProvider} ${plan.coreBenefits.network} †`
  )

  // Extended section
  toggleClass('.plan-card__extended', 'd-none', !state.showExtended)

  toggleClass(
    '.plan-summary__banner-extended',
    'd-flex',
    state.showExtended && !state.isExtendedPlan
  )
  textContent('.js-plan-name', state.planName)

  const extendedDuration = getExtendDuration(plan, state.period)

  if (state.showExtended) {
    const { addMessage } = getExtendedMessage({
      period: +state.period,
      extendedDuration,
      maxPolicies: +state.maxPolicies
    })
    if (state.period === 180) {
      const index = addMessage.indexOf('of')

      innerHTML(
        '.js-add-extended-message',
        `+ ${addMessage.substr(0, index)} <span class="d-inline-block d-md-block">
          ${addMessage.substr(index, addMessage.length - 1)} </span>`
      )
    } else {
      textContent('.js-add-extended-message', `+ ${addMessage}`)
    }
  }

  const extendedTagMessage = getExtendedTagMessage({
    period: +period,
    extendedDuration,
    currentPolicy: state.maxPolicies,
    maxPolicies: state.maxPolicies
  })
  textContent('.js-extended-tag', `${extendedTagMessage} Days Coverage`)

  if (plan.type === SURE_CARE_NAME && hcStorage.location.state_acronym === 'VA') {
    textContent('.js-extended-tag', `90 Days Coverage x2`)
  }

  // editable period
  toggleClass('#extended-tag', 'd-none', !state.isExtendedPlan)
  toggleClass('#extended-tag-disclaimer', 'd-none', !state.isExtendedPlan)
  textContent('.js-plan-start', state.periodRange.slash.start)
  textContent('.js-plan-month-start', state.periodRange.monthName.start)
  const coverageLabel = getCoverageLabel({
    plan,
    duration: period,
    policies
  })
  textContent('.js-plan-end', coverageLabel)
  toggleClass('#extended-plan', 'remove', state.isExtendedPlan)
  // duration
  textContent('.js-extended-period', period)
  toggleClass(document.body, 'period90', period === 90)

  // // summary section
  const visionPlan = state.plans[state.visionPlanId]
  toggleClass('.cost__addOns', 'd-none', totalAddOns === 0)
  textContent('.js-addon-title', state.dentalVisionTitle)
  textContent('.js-prices-addOn', prices.dental)
  textContent(
    '.js-vision-title',
    visionPlan?.productId === VISION ? '' : visionPlan?.planName
  )
  textContent('.js-prices-vision', prices.vision)
  textContent('.js-prices-total-without-fee', prices.total)
  toggleClass('[id^="cost-dental"]', 'd-none', !state.dentalVisionTitle)
  toggleClass('[id^="cost-latitude"]', 'd-none', !state.latitudeId)
  toggleClass('[id^="cost-supplemental"]', 'd-none', !state.supplementalId)
  toggleClass(
    '[id^="cost-vision"]',
    'd-none',
    !visionPlan || visionPlan.productId === VISION // Only show if not Renaissance Vision
  )
  toggleClass('.js-latitude-btn', 'remove', false)

  hcStorage.want_supplemental = false
  const latitudeId = state.latitudeId
  if (latitudeId) {
    const latitudePlan = state.plans[latitudeId]
    textContent('.js-latitude-title', latitudePlan.planName)
    textContent('.js-prices-latitude', prices.latitude)
    toggleClass(`.js-latitude-btn[data-ids="${latitudeId}"]`, 'remove', true)

    const brochureNode = getById('latitude-brochure')
    brochureNode.setAttribute('href', state.brochures[latitudePlan.brochureId])
    hcStorage.want_supplemental = true
    const latitudeTooltip = $('.js-tooltip-latitude')
    tooltip.initCustomTooltip({
      $holder: latitudeTooltip,
      customClass: 'hc-tooltip',
      content: getLatitudeFees(latitudePlan)
    })
  }

  // addons
  forEach('.add-on', node => {
    const addonId = +node.getAttribute('data-ids')
    const isSelected = state.addOns.indexOf(addonId) !== -1
    toggleClass(`.add-on[id="add-on-${addonId}"]`, 'add-on__active', isSelected)
  })

  const isNE = state.hcStorage.location.state_acronym === 'NE'

  toggleClass(document.body, 'ne', isNE)

  toggleClass(document.body, 'ne-state', isNE)

  // supplemental NRIC
  forEach('.js-supplement-input', node => {
    const id = +node.getAttribute('data-id')
    node.checked = state.addOns.indexOf(id) !== -1
  })

  if (state.supplementalId) {
    textContent('.js-prices-supplemental', prices.supplemental)
    toggleClass(`.js-supplemental[data-id="${state.supplementalId}"]`, 'remove', true)
  }

  hcStorage.want_dental_and_vision = false
  const visionPlanId = state.visionPlanId
  const visionNode = getById('vision-checkbox')

  // Check the vision box if the user has selected a Renaissance vision plan.
  // Ameritas vision plans are treated as separate add-ons.
  if (visionPlanId === VISION_ID) {
    visionNode.checked = true
    hcStorage.want_dental_and_vision = true
  }

  updateVisionControl(state.plans[14], visionNode?.checked)

  // buttons section
  toggleClass('.js-add-on-btn', 'remove', false)
  if (state.dentalPlanId) {
    const ids = [state.dentalPlanId]

    if (visionPlanId) {
      ids.push(visionPlanId)
    }
    toggleClass(`.js-add-on-btn[data-ids="${ids.join(',')}"]`, 'remove', true)
    const dentalTooltip = $('.js-tooltip-dental')
    tooltip.initCustomTooltip({
      $holder: dentalTooltip,
      customClass: 'hc-tooltip',
      content: detailsDentalFees(
        state.plans[state.dentalPlanId],
        state.plans[state.visionPlanId]
      )
    })
  }

  // Dental addons
  forEach('.js-add-on', node => {
    const id = +node.getAttribute('data-ids')
    const isSelected = state.addOns.indexOf(id) !== -1
    node.checked = isSelected

    updateDentalControl(id, state, visionNode.checked)
  })
  // Latitude addons
  forEach('.js-latitude', node => {
    const id = +node.getAttribute('data-ids')
    node.checked = state.addOns.indexOf(id) !== -1

    updateLatitudeControl(id, state)
  })

  toggleClass(document.body, 'loading', state.loading)

  // cost description tooltip
  const tooltipContent = tooltipMessage(state, plan, durationDetails)
  if (tooltipContent) {
    const tooltipCost = $('.js-tooltip-cost')

    tooltip.initCustomTooltip({
      $holder: tooltipCost,
      customClass: 'hc-tooltip',
      content: tooltipContent
    })
    toggleClass('.tooltip-cost-total', 'd-none', totalAddOns > 0)
  } else {
    toggleClass('.js-tooltip-cost', 'd-none', true)
  }

  textContent('.js-plan-month-end', getEndDateLabel())

  if (requiresAgreement(state)) {
    $('#delayed-effective-date-modal').modal('show')
  }
}

export default updateDOM
